import { Injectable, NgZone, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class UpdateService {
    constructor(
        private swUpdate: SwUpdate,
        private snackBar: MatSnackBar,
        private zone: NgZone
    ) {
        if (!this.swUpdate.isEnabled) {
            console.log('Service Worker is not enabled. UpdateService will not run.');
            return;
        }

        // Startup check once the worker controls the page (an immediate check raced the SW
        // init), so a stale cached build is noticed on launch, not only on the next foreground.
        navigator.serviceWorker.ready.then(() => setTimeout(() => this.checkForUpdates(), 5000));
        this.setupVisibilityListener();
        this.listenForUpdateFound();
        // A cached build whose files are gone from the server can't recover by itself: reload.
        this.swUpdate.unrecoverable.subscribe(() => document.location.reload());
    }

    updateAvailable = signal<boolean>(false);
    private inlinePrompts = 0;

    /** A shell that renders its own prompt from `updateAvailable` calls this; returns the undo. */
    useInlinePrompt(): () => void {
        this.inlinePrompts++;
        return () => { this.inlinePrompts = Math.max(0, this.inlinePrompts - 1); };
    }

    private checkForUpdates() {
        if (!navigator.serviceWorker.controller) {
            console.log('No Service Worker controlling the page. Skipping update check.');
            return;
        }

        console.log('Checking for updates...');
        this.swUpdate.checkForUpdate()
            .then(hasUpdate => {
                if (hasUpdate) {
                    console.log('Update found during manual check.');
                } else {
                    console.log('No update found.');
                }
            })
            .catch(err => {
                const msg = err.message || '';
                if (msg.includes('Receiving end does not exist') || msg.includes('Could not establish connection')) {
                    console.warn('SW Connection issue (benign):', msg);
                } else {
                    console.error('Error checking for updates:', err);
                }
            });
    }

    private setupVisibilityListener() {
        // efficient check when app returns to foreground
        const check = () => {
            if (document.visibilityState === 'visible') {
                this.zone.run(() => {
                    // Small delay to allow network to reconnect on mobile
                    setTimeout(() => this.checkForUpdates(), 3000);
                });
            }
        };

        document.addEventListener('visibilitychange', check);
        window.addEventListener('focus', check);
    }

    private listenForUpdateFound() {
        this.swUpdate.versionUpdates
            .pipe(
                filter(evt => evt.type === 'VERSION_READY'),
                tap(() => console.log('New version ready to be installed.'))
            )
            .subscribe(() => {
                this.updateAvailable.set(true);
                this.showUpdateNotification();
            });
    }

    private showUpdateNotification() {
        if (this.inlinePrompts > 0) return;
        const snackBarRef = this.snackBar.open('A new version is available!', 'Update Now', {
            duration: 10000, // Show for 10 seconds or until action
            panelClass: 'update-snackbar',
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
        });

        snackBarRef.onAction().subscribe(() => {
            this.activateUpdate();
        });
    }

    public activateUpdate() {
        this.swUpdate.activateUpdate()
            .then(() => document.location.reload())
            .catch(err => console.error('Error activating update:', err));
    }

    public manualCheck() {
        if (!this.swUpdate.isEnabled) {
            this.snackBar.open('Updates: Disabled (Dev Mode/No SW)', undefined, { duration: 3000 });
            return;
        }
        this.snackBar.open('Checking for updates...', undefined, { duration: 2000 });
        this.swUpdate.checkForUpdate()
            .then(hasUpdate => {
                if (hasUpdate) {
                    this.snackBar.open('Update found! Downloading...', undefined, { duration: 3000 });
                } else {
                    this.snackBar.open('App is up to date.', undefined, { duration: 3000 });
                }
            })
            .catch(err => {
                const msg = err.message || '';
                if (msg.includes('Receiving end does not exist') || msg.includes('Could not establish connection')) {
                    console.warn('SW Connection issue (benign):', msg);
                    this.snackBar.open('Service Worker not ready. Try again.', undefined, { duration: 3000 });
                } else {
                    console.error('Error checking for updates:', err);
                    this.snackBar.open('Error checking for updates.', undefined, { duration: 3000 });
                }
            });
    }
}
